import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AdminPortalShell from "@/components/admin/AdminPortalShell";
import QuestionEditorForm, {
  EMPTY_QUESTION_INPUT,
  toFormInput,
} from "@/components/admin/QuestionEditorForm";
import QuestionPreviewCard from "@/components/admin/QuestionPreviewCard";
import DeleteQuestionButton from "@/components/admin/DeleteQuestionButton";
import {
  deleteAdminQuestion,
  listAdminQuestions,
  type AdminPracticeQuestion,
  type AdminPracticeQuestionInput,
} from "@/lib/admin-api";
import { ApiError } from "@/lib/api";
import { track } from "@/lib/analytics";

/**
 * apps/web :: pages :: AdminQuestionEditorPage
 *
 * Doubles as `/admin/questions/new` (create) and `/admin/questions/:id`
 * (edit). Edit mode fetches the question on mount via `listAdminQuestions`
 * with the state filter — cheaper than adding a GET /{id} endpoint the
 * backend doesn't yet support; the list endpoint is paginated so even
 * large banks remain fast. Follow-up ticket covers a dedicated GET /{id}
 * if the bank outgrows pagination.
 *
 * Layout: two-column on lg+ (form | preview), single-column on mobile.
 * The preview reflects every keystroke via `onDraftChange`.
 */

interface LoadState {
  kind: "loading" | "ready" | "notfound" | "error";
  question?: AdminPracticeQuestion;
  errorMessage?: string;
}

export default function AdminQuestionEditorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const id = params.id;
  const isEdit = Boolean(id);
  const [load, setLoad] = useState<LoadState>(
    isEdit ? { kind: "loading" } : { kind: "ready" },
  );
  const [draft, setDraft] = useState<AdminPracticeQuestionInput>(
    EMPTY_QUESTION_INPUT,
  );
  const [deleting, setDeleting] = useState(false);
  const [redirect, setRedirect] = useState<
    | { to: string; toast?: string }
    | null
  >(null);

  useEffect(() => {
    track("admin.page_viewed", { page: isEdit ? "editor-edit" : "editor-new" });
  }, [isEdit]);

  const fetchQuestion = useCallback(async () => {
    if (!id) return;
    setLoad({ kind: "loading" });
    try {
      // Pull a wide enough window to find the question — the admin list is
      // the canonical source and the backend does not yet ship a GET /{id}.
      const page = await listAdminQuestions({ limit: 200, offset: 0 });
      const match = page.questions.find((q) => q.id === id);
      if (!match) {
        setLoad({ kind: "notfound" });
        return;
      }
      setDraft(toFormInput(match));
      setLoad({ kind: "ready", question: match });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      setLoad({ kind: "error", errorMessage: message });
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) void fetchQuestion();
  }, [isEdit, fetchQuestion]);

  const onSaved = (saved: AdminPracticeQuestion) => {
    setRedirect({
      to: "/admin/questions",
      toast: t("admin.editor.submit.saved"),
    });
    void saved;
  };

  const onDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await deleteAdminQuestion(id);
      track("admin.question.deleted", { question_id_prefix: id.slice(0, 8) });
      setRedirect({
        to: "/admin/questions",
        toast: t("admin.editor.delete.deleted"),
      });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      setLoad({ kind: "error", errorMessage: message });
    } finally {
      setDeleting(false);
    }
  };

  if (redirect) {
    return (
      <Navigate
        to={redirect.to}
        replace
        state={redirect.toast ? { toast: redirect.toast } : undefined}
      />
    );
  }

  if (load.kind === "loading") {
    return (
      <AdminPortalShell title={t("admin.editor.editTitle")}>
        <div
          role="status"
          aria-live="polite"
          data-testid="admin-editor-loading"
          className="rounded-md border border-neutral-200 bg-white p-lg text-center text-sm text-neutral-700"
        >
          {t("admin.editor.loading")}
        </div>
      </AdminPortalShell>
    );
  }

  if (load.kind === "notfound") {
    return (
      <AdminPortalShell title={t("admin.editor.editTitle")}>
        <div
          role="alert"
          data-testid="admin-editor-notfound"
          className="rounded-md border border-danger/40 bg-danger/5 p-md text-sm text-danger"
        >
          {t("admin.editor.notFound")}
        </div>
      </AdminPortalShell>
    );
  }

  if (load.kind === "error") {
    return (
      <AdminPortalShell title={t("admin.editor.editTitle")}>
        <div
          role="alert"
          data-testid="admin-editor-loaderror"
          className="rounded-md border border-danger/40 bg-danger/5 p-md text-sm text-danger"
        >
          {t("admin.editor.loadError", { message: load.errorMessage })}
        </div>
        <button
          type="button"
          onClick={() => void fetchQuestion()}
          className="mt-md inline-flex items-center rounded-md border border-brand-primary bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("exam.retry")}
        </button>
      </AdminPortalShell>
    );
  }

  const initial = isEdit ? draft : EMPTY_QUESTION_INPUT;

  return (
    <AdminPortalShell
      title={isEdit ? t("admin.editor.editTitle") : t("admin.editor.createTitle")}
    >
      <div className="grid grid-cols-1 gap-lg lg:grid-cols-5">
        <div className="lg:col-span-3">
          <QuestionEditorForm
            mode={isEdit ? "edit" : "create"}
            questionId={id}
            initial={initial}
            onSaved={onSaved}
            onDraftChange={setDraft}
          />
          {isEdit && (
            <div className="mt-lg flex justify-end">
              <DeleteQuestionButton
                onConfirm={onDelete}
                pending={deleting}
                label={t("admin.editor.delete.cta")}
              />
            </div>
          )}
          <div className="mt-lg">
            <button
              type="button"
              onClick={() => navigate("/admin/questions")}
              className="text-sm text-brand-primary underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              {t("admin.editor.delete.cancel")}
            </button>
          </div>
        </div>
        <div className="lg:col-span-2">
          <QuestionPreviewCard question={draft} />
        </div>
      </div>
    </AdminPortalShell>
  );
}
